import {rootState} from '@mintdata-wsp/common/state/RootState';
import {MeshBounds} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshBounds';
import {MeshUtils} from '@mintdata-wsp/common/util/MeshUtils';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {inject, observer} from 'mobx-react';
import React from 'react';
import * as classnames from 'classnames/bind';

const styles = require('./MeshActiveUserSelections.less');
const cx = classnames.bind(styles);

interface MeshActiveUserSelectionsProps
{
    ctx?:IWSPMeshContext;
}

@inject('ctx')
@observer
export class MeshActiveUserSelections extends React.Component<MeshActiveUserSelectionsProps>
{
    public render():JSX.Element
    {
        const {activeUsers} = rootState.ui.visualDesigner;
        const activeSheet = controllers.ui.spreadsheets.main.getActiveSheet();
        const {ctx} = this.props;
        const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

        if( !activeUsers.otherUsers.length || !activeSheet )
            return null;

        // mobx: force refresh
        activeUsers.otherUsersDetails.size;

        let usernames = [];
        for( const user of activeUsers.otherUsers )
        {
            if( !user.range )
                continue;

            const userDetails = activeUsers.otherUsersDetails.get(user.userId);
            if( !userDetails )
                return null;

            const cellPositions = this._parseRangeKey(user.range);

            let isHovered = false;
            if( meshState.details.cellAtCursor )
            {
                const {colIndex, rowIndex} = meshState.details.cellAtCursor;
                isHovered = colIndex >= cellPositions[0] && colIndex <= cellPositions[2] &&
                        rowIndex >= cellPositions[1] && rowIndex <= cellPositions[3];
            }
            if( meshState.isHovered && isHovered )
                usernames.push(userDetails.fullName);
        }

        return <div className={cx('activeUsersContainer')}>
            {
                activeUsers.otherUsers.filter(user => user.sheet === activeSheet.id).map(user => {

                    if( !user.range )
                        return null;
                        
                    const userDetails = activeUsers.otherUsersDetails.get(user.userId);
                    
                    if( !userDetails )
                        return null;

                    // 1_5:1_5 -- col_row
                    // 1_null:3_null - selected cols 1:3
                    // null_1:null_3 - selected rows 1:3
                    const cellPositions = this._parseRangeKey(user.range);

                    const bounds = MeshUtils.getRangeBounds2(meshState, cellPositions[1], cellPositions[0], cellPositions[3], cellPositions[2]);

                    let isHovered = false;
                    if( meshState.details.cellAtCursor )
                    {
                        const {colIndex, rowIndex} = meshState.details.cellAtCursor;
                        isHovered = meshState.isHovered && colIndex >= cellPositions[0] && colIndex <= cellPositions[2] &&
                                rowIndex >= cellPositions[1] && rowIndex <= cellPositions[3];
                    }

                    return <div key={user.userId}
                        style={this._calculateBorderStyle(bounds, userDetails.color)}
                        className={cx('activeUserSelection')}
                    >
                        <div style={this._calculateCaptionStyle(isHovered, cellPositions, userDetails.color)} className={cx('selectionTitle')}>
                            {usernames.join(', ')}
                        </div>
                    </div>
                })
            }
        </div>
        
    }

    private _parseRangeKey(key:string)
    {
        const {ctx} = this.props;
        const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);
        const cellPositions = key.split(/[_:]/).map(x => Number(x));

        if( Number.isNaN(cellPositions[0]) )
            cellPositions[0] = 0;
        
        if( Number.isNaN(cellPositions[1]) )
            cellPositions[1] = 0;

        if( Number.isNaN(cellPositions[2]) )
            cellPositions[2] = meshState.size.numCols - 1;

        if( Number.isNaN(cellPositions[3]) )
            cellPositions[3] = meshState.size.numRows - 1;

        return cellPositions;
    }

    private _calculateBorderStyle(bounds:MeshBounds, color:string):React.CSSProperties
    {
        const {x, y, width, height} = bounds;
        return {
            transform: `translate(${x}px, ${y}px)`,
            width: `${width}px`,
            height: `${height}px`,
            borderColor: color
        };
    }

    private _calculateCaptionStyle(isHovered: boolean, range:number[], color:string):React.CSSProperties
    {
        const {ctx} = this.props;
        const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

        const {visibleStartColIndex, visibleStartRowIndex, visibleEndColIndex, visibleEndRowIndex} = meshState;

        const selectionStartCol = range[0];
        const selectionStartRow = range[1];
        const selectionEndCol = range[2];
        const selectionEndRow = range[3];

        const calcLeftPosition = () =>
        {
            if( selectionStartCol <= visibleStartColIndex )
                return `${meshState.colWidths.slice(selectionStartCol, visibleStartColIndex)
                    .reduce((sum, value) => sum += value, 0)}px`;
        }

        const calcRightPosition = () =>
        {
            if( selectionStartCol > visibleStartColIndex )
                return `${meshState.colWidths.slice(Math.max(visibleEndColIndex-1, selectionStartCol), selectionEndCol)
                    .reduce((sum, value) => sum += value, 0)}px`;
        }

        const calcTopPosition = () =>
        {
            // case 1: selection end is beyond the visible area
            // case 2: whole selection is visible, but not touching the top line
            if( selectionEndRow > visibleEndRowIndex - 2 || selectionStartRow > visibleStartRowIndex && selectionEndRow < visibleEndRowIndex )
            {
                const shift = selectionStartRow > visibleStartRowIndex ? -20 : 0;
                return `${meshState.rowHeights.slice(selectionStartRow, visibleStartRowIndex)
                    .reduce((sum, value) => sum += value, 0) + shift}px`;
            }
        }

        const calcBottomPosition = () =>
        {
            // case 1: selection start is at the top edge, or beyond the visible area
            if( selectionStartRow <= visibleStartRowIndex && selectionEndRow <= visibleEndRowIndex - 2 )
            {
                const shift = selectionEndRow < visibleEndRowIndex ? -20 : 0;
                return `${meshState.rowHeights.slice(visibleEndRowIndex, selectionEndRow)
                    .reduce((sum, value) => sum += value, 0) + shift}px`;
            }
        }

        if( isHovered )
            return {
                display: 'block',
                top: calcTopPosition(),
                bottom: calcBottomPosition(),
                right: calcRightPosition(),
                left: calcLeftPosition(),
                backgroundColor: color
            }
    }
}